import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../App";
import {
  Upload, Download, Zap, FileText, Shield, Calendar,
  Check, ChevronDown, ArrowRight, Lock
} from "lucide-react";

const GREEN = "#1A4D2E";
const AMBER = "#F59E0B";
const LEAF = "M24,40 C12,32 8,20 16,10 C20,4 28,4 32,10 C40,20 36,32 24,40Z";
const W = 1152; // global max-width

function Logo({ size = 36 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      <rect width="48" height="48" rx="12" fill={GREEN} />
      <path d={LEAF} fill="white" opacity="0.95" />
      <line x1="24" y1="38" x2="24" y2="12" stroke={GREEN} strokeWidth="2" strokeLinecap="round" opacity="0.45" />
    </svg>
  );
}

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: "1px solid #E5E7EB" }}>
      <button
        onClick={() => setOpen(!open)}
        style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 0", textAlign: "left", background: "none", border: "none", cursor: "pointer", gap: 16 }}
      >
        <span style={{ fontWeight: 600, color: "#111827", fontSize: 15, lineHeight: 1.4 }}>{q}</span>
        <ChevronDown size={18} color="#6B7280" style={{ flexShrink: 0, transform: open ? "rotate(180deg)" : "none", transition: "transform 0.25s" }} />
      </button>
      {open && (
        <div style={{ paddingBottom: 18 }}>
          <p style={{ color: "#4B5563", lineHeight: 1.7, fontSize: 14, margin: 0 }}>{a}</p>
        </div>
      )}
    </div>
  );
}

function DashboardMockup() {
  const txs = [
    { d: "TRANSPORT NSW ETOLL", c: "D2 Work travel", a: "-$25.05", ok: true },
    { d: "AUSSIE BROADBAND", c: "D5 Work-related", a: "-$79.00", ok: true },
    { d: "WOOLWORTHS 0965", c: "Groceries (personal)", a: "-$121.15", ok: false },
    { d: "KENNARDS SELF STORAGE", c: "D11 Rental property", a: "-$226.00", ok: true },
  ];
  return (
    <div style={{ position: "relative", maxWidth: 780, margin: "52px auto 0", userSelect: "none" }}>
      <div style={{ background: "#fff", borderRadius: 20, boxShadow: "0 32px 80px rgba(0,0,0,0.13)", border: "1px solid #E5E7EB", overflow: "hidden" }}>
        {/* Browser chrome */}
        <div style={{ background: "#F3F4F6", borderBottom: "1px solid #E5E7EB", padding: "10px 16px", display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ display: "flex", gap: 5 }}>
            {["#FC8181", "#F6AD55", "#68D391"].map(c => <div key={c} style={{ width: 11, height: 11, borderRadius: "50%", background: c }} />)}
          </div>
          <div style={{ flex: 1, background: "#fff", borderRadius: 6, padding: "4px 12px", textAlign: "center", fontSize: 11, color: "#9CA3AF", border: "1px solid #E5E7EB" }}>
            taxbuddy.au/dashboard
          </div>
        </div>
        {/* App */}
        <div style={{ display: "flex", minHeight: 290 }}>
          <div style={{ width: 140, background: GREEN, padding: 10, flexShrink: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 4px 14px" }}>
              <Logo size={20} /><span style={{ color: "#fff", fontWeight: 800, fontSize: 10 }}>TaxBuddy</span>
            </div>
            {[["Dashboard", true], ["Upload", false], ["Transactions", false], ["Reports", false], ["Settings", false]].map(([l, a]) => (
              <div key={l} style={{ padding: "6px 8px", borderRadius: 6, marginBottom: 1, background: a ? "rgba(255,255,255,0.2)" : "transparent", color: a ? "#fff" : "rgba(255,255,255,0.55)", fontSize: 10, fontWeight: a ? 700 : 400 }}>{l}</div>
            ))}
          </div>
          <div style={{ flex: 1, background: "#F8FAF9", padding: 16 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 14 }}>
              <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, padding: "11px 14px" }}>
                <div style={{ fontSize: 17, fontWeight: 900, color: "#111827" }}>$62,400</div>
                <div style={{ fontSize: 9, color: "#6B7280", marginTop: 2 }}>Income</div>
              </div>
              <div style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, padding: "11px 14px" }}>
                <div style={{ fontSize: 17, fontWeight: 900, color: "#374151" }}>$18,230</div>
                <div style={{ fontSize: 9, color: "#6B7280", marginTop: 2 }}>Expenses</div>
              </div>
              <div style={{ background: "#F0FDF4", border: "1px solid #86EFAC", borderRadius: 12, padding: "11px 14px" }}>
                <div style={{ fontSize: 17, fontWeight: 900, color: GREEN }}>$8,240</div>
                <div style={{ fontSize: 9, color: "#6B7280", marginTop: 2 }}>Deductions</div>
              </div>
            </div>
            <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E5E7EB", overflow: "hidden" }}>
              <div style={{ padding: "8px 14px", borderBottom: "1px solid #F3F4F6", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 9, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>Recent Transactions</span>
                <span style={{ fontSize: 9, color: GREEN, fontWeight: 600 }}>View all →</span>
              </div>
              {txs.map((t, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 14px", borderBottom: i < txs.length - 1 ? "1px solid #F9FAFB" : "none" }}>
                  <div>
                    <div style={{ fontSize: 9, fontWeight: 600, color: "#111827" }}>{t.d}</div>
                    <span style={{ fontSize: 8, padding: "2px 6px", borderRadius: 20, fontWeight: 600, marginTop: 2, display: "inline-block", background: t.ok ? "#F0FDF4" : "#F3F4F6", color: t.ok ? GREEN : "#9CA3AF" }}>{t.c}</span>
                  </div>
                  <span style={{ fontSize: 9, fontWeight: 700, color: "#374151" }}>{t.a}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div style={{ position: "absolute", bottom: -14, right: -10, background: "#fff", borderRadius: 14, boxShadow: "0 8px 32px rgba(26,77,46,0.18)", border: "1px solid #D1FAE5", padding: "8px 14px", display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ width: 26, height: 26, background: "#F0FDF4", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Check size={14} color={GREEN} strokeWidth={3} />
        </div>
        <div>
          <div style={{ fontSize: 10, fontWeight: 800, color: "#111827" }}>1,143 transactions</div>
          <div style={{ fontSize: 9, color: "#6B7280" }}>auto-categorised</div>
        </div>
      </div>
    </div>
  );
}

const BANKS = [
  "ING", "CommBank", "Bendigo Bank", "Westpac", "ANZ", "NAB", "Up Bank",
  "Macquarie", "St George", "Bank of Melbourne", "BankSA", "Bankwest",
  "Suncorp", "Bank of Queensland", "ME Bank", "Great Southern Bank",
  "Teachers Mutual", "Newcastle Permanent", "Heritage Bank", "People's Choice",
];

const PAIN_CARDS = [
  { emoji: "📦", title: "Shoebox of receipts at tax time", desc: "Scrambling through 12 months of transactions in June because you never tracked anything during the year." },
  { emoji: "❓", title: "No idea what you can claim", desc: "You know you probably have deductions but you're not sure which expenses qualify and which don't." },
  { emoji: "💸", title: "Paying your accountant to sort your paperwork", desc: "Your accountant's hourly rate is doing work that software should be doing — and the bill shows it." },
];

const STEPS = [
  { n: "1", icon: Upload, title: "Upload your PDF bank statements", desc: "Export your statement from online banking and upload it. 20+ Australian banks supported." },
  { n: "2", icon: Zap, title: "TaxBuddy categorises everything automatically", desc: "Every transaction is matched to an ATO deduction category. Transfers, duplicates, and personal expenses are handled automatically." },
  { n: "3", icon: Download, title: "Download your accountant-ready Excel report", desc: "A clean workbook with a deductions-only sheet and ATO category codes — ready to hand straight to your tax agent." },
];

const FEATURES = [
  { icon: FileText, title: "Auto-categorisation against ATO categories", desc: "Every transaction matched to the correct ATO deduction code from D1 to D12, plus rental and investment categories." },
  { icon: Shield, title: "Duplicate detection", desc: "Upload the same statement twice and TaxBuddy catches it. No doubled-up totals on your report." },
  { icon: Zap, title: "Quick Sort for manual review", desc: "Work through uncategorised transactions one at a time and assign the right category in seconds." },
  { icon: Check, title: "Tax deduction identification", desc: "Deductible expenses are highlighted in every view so nothing gets left off your return." },
  { icon: Calendar, title: "Calendar view of spending", desc: "See transactions on a calendar to spot patterns and recurring charges across the financial year." },
  { icon: Download, title: "Accountant-ready Excel export", desc: "One workbook: summary sheet, all transactions, and a deductions-only sheet with ATO codes." },
];

const CHECKLIST = [
  "TaxBuddy categorises transactions — it does not lodge your tax return.",
  "Results should be reviewed with a registered tax agent.",
  "You need PDF bank statements — not CSV files or screenshots.",
  "Supported banks: ING, CBA, Bendigo, Westpac, ANZ, NAB, Up Bank, and more.",
  "Refunds are not available after your first export.",
];

const PRICING = [
  {
    name: "Free", price: "$0", period: "", desc: "Upload, extract, and review all your transactions. No card required.",
    features: ["Unlimited statement uploads", "Automatic ATO categorisation", "All 12 ATO deduction categories", "In-app review and editing", "Duplicate detection"],
    locked: ["Excel workbook export"], cta: "Get started — free", badge: null, badgeColor: null, featured: false,
  },
  {
    name: "Pro Monthly", price: "$9", period: "/month", desc: "Everything in Free, plus the accountant-ready Excel workbook.",
    features: ["Everything in Free", "Excel workbook export (3-sheet)", "ATO deduction codes on every row", "Accountant-ready formatting", "Priority support"],
    locked: [], cta: "Start Pro — $9/month", badge: "Most Popular", badgeColor: AMBER, featured: true,
  },
  {
    name: "Pro Annual", price: "$69", period: "/year", desc: "$5.75/month billed annually — save $39 vs monthly.",
    features: ["Everything in Pro Monthly", "14-day automatic refund", "Early access to new features"],
    locked: [], cta: "Get annual — best value", badge: "Best Value", badgeColor: GREEN, featured: false,
  },
];

const FAQS = [
  { q: "What banks are supported?", a: "ING, CommBank (CBA), Bendigo Bank, Westpac, ANZ, NAB, Up Bank, Macquarie, St George, Bank of Melbourne, BankSA, Bankwest, Suncorp, Bank of Queensland, ME Bank, Great Southern Bank, Teachers Mutual, Newcastle Permanent, Heritage Bank, and People's Choice. More added regularly. Email findjoy143@gmail.com if yours isn't listed — we'll add support within 48 hours." },
  { q: "Is my data secure?", a: "Your data is encrypted at rest (AES-256) and in transit (TLS). We don't store your original PDFs after processing. We don't sell your data. You can delete your account and all data at any time. TaxBuddy complies with the Australian Privacy Act 1988." },
  { q: "What is the difference between free and pro?", a: "Free lets you upload statements, extract transactions, and review every ATO category assignment inside the app — completely free, no card required. Pro ($9/month or $69/year) adds the Excel workbook export: a formatted, accountant-ready file with ATO codes, a summary sheet, and a deductions-only sheet." },
  { q: "Do I need an accountant?", a: "TaxBuddy doesn't replace a registered tax agent — it organises your records so that when you do see one, you walk in prepared instead of handing over a folder of statements. Many users find their accountant meetings get shorter because the categorisation work is already done." },
  { q: "Can I use this for a business?", a: "TaxBuddy is designed for individuals including sole traders. It handles business-related deductions like software subscriptions, bank fees, home office costs, equipment, and professional development. Larger businesses with complex structures (companies, trusts) should use dedicated accounting software." },
  { q: "Why does support go to findjoy143@gmail.com?", a: "TaxBuddy is built by FindJoy143, a small Australian indie software company. The '143' is a Filipino pager code that means 'I love you' — 1 letter, 4 letters, 3 letters. It reflects the heart behind everything we build. We're a small team and findjoy143@gmail.com is how you reach us directly." },
];

export default function LandingPage() {
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const FF = "'Plus Jakarta Sans', system-ui, sans-serif";

  return (
    <div style={{ minHeight: "100vh", background: "#F9FAFB", fontFamily: FF }}>

      {/* Marquee animation */}
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes bank-scroll { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        .bank-marquee { display: flex; gap: 12px; width: max-content; animation: bank-scroll 40s linear infinite; }
        .bank-marquee:hover { animation-play-state: paused; }
        .bank-mask { overflow: hidden; -webkit-mask-image: linear-gradient(to right, transparent, black 8%, black 92%, transparent); mask-image: linear-gradient(to right, transparent, black 8%, black 92%, transparent); }
      `}} />

      {/* ── 1. NAV ─────────────────────────────────────────────────────────── */}
      <nav style={{
        position: "sticky", top: 0, zIndex: 50,
        background: scrolled ? "rgba(255,255,255,0.97)" : "#fff",
        backdropFilter: scrolled ? "blur(8px)" : "none",
        borderBottom: "1px solid #E5E7EB",
        boxShadow: scrolled ? "0 2px 16px rgba(0,0,0,0.06)" : "none",
        transition: "box-shadow 0.25s",
      }}>
        <div style={{ maxWidth: W, margin: "0 auto", padding: "0 32px", height: 64, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
          <Link to="/" style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none", flexShrink: 0 }}>
            <Logo size={34} />
            <span style={{ fontWeight: 900, fontSize: 18, color: "#111827", letterSpacing: "-0.3px" }}>TaxBuddy</span>
          </Link>
          <div style={{ display: "flex", gap: 28 }}>
            <Link to="/about" style={{ fontSize: 14, fontWeight: 500, color: "#4B5563", textDecoration: "none" }}>About</Link>
            <Link to="/affiliate" style={{ fontSize: 14, fontWeight: 500, color: "#4B5563", textDecoration: "none" }}>Partners</Link>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
            {user ? (
              <Link to="/dashboard" style={{ padding: "9px 20px", background: GREEN, color: "#fff", borderRadius: 10, textDecoration: "none", fontWeight: 700, fontSize: 14 }}>Dashboard →</Link>
            ) : (
              <>
                <Link to="/login" style={{ padding: "9px 18px", color: "#374151", borderRadius: 10, textDecoration: "none", fontWeight: 600, fontSize: 14, border: "1px solid #E5E7EB", background: "#fff" }}>Sign In</Link>
                <Link to="/signup" style={{ padding: "9px 20px", background: GREEN, color: "#fff", borderRadius: 10, textDecoration: "none", fontWeight: 700, fontSize: 14 }}>Get started free →</Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* ── 2. HERO ────────────────────────────────────────────────────────── */}
      <section style={{ maxWidth: W, margin: "0 auto", padding: "72px 32px 24px", textAlign: "center" }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#F0FDF4", border: "1px solid #86EFAC", borderRadius: 40, padding: "6px 16px", marginBottom: 28 }}>
          <span style={{ width: 7, height: 7, borderRadius: "50%", background: GREEN, display: "inline-block" }} />
          <span style={{ fontSize: 13, fontWeight: 700, color: GREEN }}>🇦🇺 Australian tax categorisation tool</span>
        </div>
        <h1 style={{ fontSize: 62, fontWeight: 900, color: "#111827", margin: "0 auto 20px", lineHeight: 1.05, letterSpacing: "-2px", maxWidth: 720 }}>
          Your tax.<br />
          <span style={{ color: GREEN }}>Sorted.</span>
        </h1>
        <p style={{ fontSize: 19, color: "#6B7280", margin: "0 auto 36px", lineHeight: 1.65, maxWidth: 560 }}>
          Upload your bank statements. TaxBuddy automatically categorises every transaction for the ATO — ready for your tax agent in minutes.
        </p>
        <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap", marginBottom: 14 }}>
          <Link to="/signup" style={{ padding: "15px 32px", background: GREEN, color: "#fff", borderRadius: 14, textDecoration: "none", fontWeight: 800, fontSize: 16, display: "inline-flex", alignItems: "center", gap: 8 }}>
            Upload my first statement free →
          </Link>
          <Link to="/login" style={{ padding: "15px 28px", background: "#fff", color: GREEN, border: `2px solid ${GREEN}`, borderRadius: 14, textDecoration: "none", fontWeight: 700, fontSize: 16 }}>
            Sign in
          </Link>
        </div>
        <p style={{ fontSize: 12, color: "#9CA3AF", margin: 0 }}>
          Free plan available · Australian-made · No lock-in
        </p>
        <DashboardMockup />
      </section>

      {/* ── 3. BANK LOGOS STRIP ─────────────────────────────────────────────── */}
      <section style={{ background: "#fff", borderTop: "1px solid #E5E7EB", borderBottom: "1px solid #E5E7EB", padding: "40px 0", marginTop: 72 }}>
        <p style={{ fontSize: 11, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: "1.5px", margin: "0 0 24px", textAlign: "center" }}>Works with your bank</p>
        <div className="bank-mask">
          <div className="bank-marquee">
            {[...BANKS, ...BANKS].map((b, i) => (
              <span key={i} style={{ padding: "9px 20px", background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 10, fontSize: 13, fontWeight: 600, color: "#374151", whiteSpace: "nowrap", flexShrink: 0 }}>{b}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ── 4. PAIN SECTION ─────────────────────────────────────────────────── */}
      <section style={{ background: GREEN, padding: "88px 32px" }}>
        <div style={{ maxWidth: W, margin: "0 auto", textAlign: "center" }}>
          <h2 style={{ fontSize: 44, fontWeight: 900, color: "#fff", margin: "0 0 12px", letterSpacing: "-0.5px" }}>Sound familiar?</h2>
          <p style={{ fontSize: 17, color: "rgba(255,255,255,0.65)", margin: "0 0 52px" }}>Tax time shouldn't feel like this every year.</p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20, marginBottom: 52 }}>
            {PAIN_CARDS.map(p => (
              <div key={p.title} style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 18, padding: "30px 28px", textAlign: "left" }}>
                <div style={{ fontSize: 38, marginBottom: 16 }}>{p.emoji}</div>
                <div style={{ fontWeight: 800, fontSize: 19, color: "#fff", marginBottom: 10, lineHeight: 1.3 }}>{p.title}</div>
                <div style={{ fontSize: 14, color: "rgba(255,255,255,0.65)", lineHeight: 1.7 }}>{p.desc}</div>
              </div>
            ))}
          </div>
          <Link to="/signup" style={{ display: "inline-flex", alignItems: "center", gap: 10, padding: "16px 36px", background: AMBER, color: "#fff", borderRadius: 14, textDecoration: "none", fontWeight: 800, fontSize: 17 }}>
            There's a better way <ArrowRight size={18} />
          </Link>
        </div>
      </section>

      {/* ── 5. HOW IT WORKS ─────────────────────────────────────────────────── */}
      <section style={{ background: "#fff", padding: "88px 32px" }}>
        <div style={{ maxWidth: W, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 60 }}>
            <p style={{ fontSize: 11, fontWeight: 800, color: GREEN, textTransform: "uppercase", letterSpacing: "2px", margin: "0 0 14px" }}>Three steps · Takes minutes</p>
            <h2 style={{ fontSize: 44, fontWeight: 900, color: "#111827", margin: "0 0 14px", letterSpacing: "-0.5px" }}>How it works</h2>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 40 }}>
            {STEPS.map(({ n, icon: Icon, title, desc }) => (
              <div key={n} style={{ position: "relative" }}>
                <div style={{ position: "relative", width: 56, height: 56, marginBottom: 22 }}>
                  <div style={{ width: 56, height: 56, background: GREEN, borderRadius: 18, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Icon size={26} color="#fff" />
                  </div>
                  <div style={{ position: "absolute", top: -8, left: -8, width: 24, height: 24, background: AMBER, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 900, color: "#fff" }}>{n}</div>
                </div>
                <h3 style={{ fontSize: 19, fontWeight: 800, color: "#111827", marginBottom: 10, lineHeight: 1.3 }}>{title}</h3>
                <p style={{ fontSize: 14, color: "#6B7280", lineHeight: 1.7, margin: 0 }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 6. FEATURES GRID ────────────────────────────────────────────────── */}
      <section style={{ background: "#F9FAFB", padding: "88px 32px" }}>
        <div style={{ maxWidth: W, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 52 }}>
            <p style={{ fontSize: 11, fontWeight: 800, color: GREEN, textTransform: "uppercase", letterSpacing: "2px", margin: "0 0 14px" }}>Everything you need</p>
            <h2 style={{ fontSize: 44, fontWeight: 900, color: "#111827", margin: "0 0 14px", letterSpacing: "-0.5px" }}>Built for Australian tax time</h2>
            <p style={{ fontSize: 16, color: "#6B7280", maxWidth: 500, margin: "0 auto" }}>Every feature designed to make the end-of-financial-year scramble less painful.</p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 20 }}>
            {FEATURES.map(({ icon: Icon, title, desc }) => (
              <div key={title} style={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 16, padding: "26px 24px" }}>
                <div style={{ width: 44, height: 44, background: "#F0FDF4", borderRadius: 13, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16 }}>
                  <Icon size={21} color={GREEN} />
                </div>
                <div style={{ fontWeight: 700, fontSize: 15, color: "#111827", marginBottom: 8 }}>{title}</div>
                <div style={{ fontSize: 13, color: "#6B7280", lineHeight: 1.65 }}>{desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 7. BEFORE YOU SUBSCRIBE CHECKLIST ───────────────────────────────── */}
      <section style={{ background: "#FFFBEB", borderTop: "1px solid #FDE68A", borderBottom: "1px solid #FDE68A", padding: "72px 32px" }}>
        <div style={{ maxWidth: 760, margin: "0 auto" }}>
          <div style={{ marginBottom: 32 }}>
            <p style={{ fontSize: 11, fontWeight: 800, color: "#92400E", textTransform: "uppercase", letterSpacing: "2px", margin: "0 0 12px" }}>Honest disclosure</p>
            <h2 style={{ fontSize: 36, fontWeight: 900, color: "#111827", margin: "0 0 8px" }}>Before you subscribe, please confirm:</h2>
            <p style={{ fontSize: 14, color: "#6B7280", margin: 0 }}>TaxBuddy is designed for specific use cases. Make sure it's right for you.</p>
          </div>
          <div style={{ background: "#fff", border: "1px solid #FDE68A", borderRadius: 18, padding: "8px 28px" }}>
            {CHECKLIST.map((item, i) => (
              <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 14, padding: "16px 0", borderBottom: i < CHECKLIST.length - 1 ? "1px solid #FEF9C3" : "none" }}>
                <div style={{ width: 24, height: 24, background: "#FEF3C7", border: "2px solid #F59E0B", borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
                  <Check size={13} color={AMBER} strokeWidth={3} />
                </div>
                <span style={{ fontSize: 15, color: "#374151", lineHeight: 1.55 }}>{item}</span>
              </div>
            ))}
          </div>
          <p style={{ fontSize: 12, color: "#9CA3AF", marginTop: 16 }}>
            By subscribing you confirm you have read and understand the above. Australian Consumer Law guarantees apply to all paid plans.
          </p>
        </div>
      </section>

      {/* ── 8. PRICING ──────────────────────────────────────────────────────── */}
      <section style={{ background: "#fff", padding: "88px 32px" }}>
        <div style={{ maxWidth: W, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 56 }}>
            <p style={{ fontSize: 11, fontWeight: 800, color: GREEN, textTransform: "uppercase", letterSpacing: "2px", margin: "0 0 14px" }}>Clear honest pricing</p>
            <h2 style={{ fontSize: 44, fontWeight: 900, color: "#111827", margin: "0 0 12px", letterSpacing: "-0.5px" }}>Free to start. Export when you're ready.</h2>
            <p style={{ fontSize: 16, color: "#6B7280", margin: 0 }}>Review everything for free. Pro unlocks the accountant-ready Excel export.</p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 20, alignItems: "center", maxWidth: 1000, margin: "0 auto" }}>
            {PRICING.map(({ name, price, period, desc, features, locked, cta, badge, badgeColor, featured }) => (
              <div key={name} style={{ position: "relative", background: featured ? GREEN : "#fff", border: featured ? "none" : "1px solid #E5E7EB", borderRadius: 22, padding: "36px 30px", display: "flex", flexDirection: "column", boxShadow: featured ? "0 24px 64px rgba(26,77,46,0.28)" : "0 2px 12px rgba(0,0,0,0.04)" }}>
                {badge && (
                  <div style={{ position: "absolute", top: -14, left: "50%", transform: "translateX(-50%)", background: badgeColor, color: "#fff", fontSize: 11, fontWeight: 800, padding: "5px 16px", borderRadius: 20, whiteSpace: "nowrap" }}>
                    {badge}
                  </div>
                )}
                <div style={{ marginBottom: 24 }}>
                  <h3 style={{ fontWeight: 800, fontSize: 17, color: featured ? "#fff" : "#111827", margin: "0 0 8px" }}>{name}</h3>
                  <div style={{ display: "flex", alignItems: "flex-end", gap: 4, marginBottom: 8 }}>
                    <span style={{ fontSize: 48, fontWeight: 900, color: featured ? "#fff" : "#111827", lineHeight: 1 }}>{price}</span>
                    <span style={{ fontSize: 14, color: featured ? "rgba(255,255,255,0.55)" : "#9CA3AF", marginBottom: 8 }}>{period}</span>
                  </div>
                  <p style={{ fontSize: 13, color: featured ? "rgba(255,255,255,0.7)" : "#6B7280", lineHeight: 1.55, margin: 0 }}>{desc}</p>
                </div>
                <ul style={{ flex: 1, listStyle: "none", padding: 0, margin: "0 0 28px" }}>
                  {features.map(f => (
                    <li key={f} style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 11 }}>
                      <div style={{ width: 18, height: 18, borderRadius: "50%", background: featured ? "rgba(255,255,255,0.2)" : "#F0FDF4", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
                        <Check size={10} color={featured ? "#fff" : GREEN} strokeWidth={3} />
                      </div>
                      <span style={{ fontSize: 13, color: featured ? "rgba(255,255,255,0.9)" : "#374151", lineHeight: 1.45 }}>{f}</span>
                    </li>
                  ))}
                  {locked.map(f => (
                    <li key={f} style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 11 }}>
                      <div style={{ width: 18, height: 18, borderRadius: "50%", background: "#F3F4F6", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 1 }}>
                        <Lock size={9} color="#9CA3AF" />
                      </div>
                      <span style={{ fontSize: 13, color: "#9CA3AF", lineHeight: 1.45 }}>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link to="/signup" style={{ display: "block", textAlign: "center", padding: "14px", background: featured ? AMBER : GREEN, color: "#fff", borderRadius: 12, textDecoration: "none", fontWeight: 800, fontSize: 14 }}>
                  {cta}
                </Link>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 28, marginTop: 44 }}>
            {["No card required for free plan", "Cancel anytime", "Australian Consumer Law applies"].map(t => (
              <div key={t} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#6B7280" }}>
                <Check size={14} color={GREEN} />{t}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 9. EARLY USERS / BE FIRST ───────────────────────────────────────── */}
      <section style={{ background: "#F9FAFB", padding: "88px 32px" }}>
        <div style={{ maxWidth: W, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <p style={{ fontSize: 11, fontWeight: 800, color: GREEN, textTransform: "uppercase", letterSpacing: "2px", margin: "0 0 14px" }}>Early users</p>
            <h2 style={{ fontSize: 44, fontWeight: 900, color: "#111827", margin: "0 0 16px", letterSpacing: "-0.5px", lineHeight: 1.1 }}>Be one of our first reviewers</h2>
            <p style={{ fontSize: 18, color: "#6B7280", maxWidth: 560, margin: "0 auto", lineHeight: 1.65 }}>
              TaxBuddy is newly launched. We're looking for our first 50 users to share their experience.
            </p>
          </div>
          <div style={{ maxWidth: 620, margin: "0 auto" }}>
            <div style={{ background: "#fff", border: `2px solid ${GREEN}`, borderRadius: 22, padding: "40px 36px", textAlign: "center" }}>
              <div style={{ width: 56, height: 56, background: "#F0FDF4", borderRadius: 18, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
                <Upload size={26} color={GREEN} />
              </div>
              <h3 style={{ fontSize: 22, fontWeight: 800, color: "#111827", margin: "0 0 12px", lineHeight: 1.3 }}>
                Try it free and tell us what you think
              </h3>
              <p style={{ fontSize: 15, color: "#6B7280", lineHeight: 1.65, margin: "0 0 28px" }}>
                Upload your bank statements, explore the auto-categorisation, and let us know what works and what doesn't. Your feedback directly shapes the product.
              </p>
              <Link to="/signup" style={{ display: "inline-flex", alignItems: "center", gap: 10, padding: "15px 32px", background: GREEN, color: "#fff", borderRadius: 13, textDecoration: "none", fontWeight: 800, fontSize: 16 }}>
                Upload my first statement free <ArrowRight size={18} />
              </Link>
              <p style={{ fontSize: 12, color: "#9CA3AF", marginTop: 16 }}>
                No credit card · Takes 30 seconds to sign up · Email us your feedback at findjoy143@gmail.com
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── 10. FAQ ─────────────────────────────────────────────────────────── */}
      <section style={{ background: "#fff", padding: "88px 32px" }}>
        <div style={{ maxWidth: 760, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 52 }}>
            <p style={{ fontSize: 11, fontWeight: 800, color: GREEN, textTransform: "uppercase", letterSpacing: "2px", margin: "0 0 14px" }}>Got questions?</p>
            <h2 style={{ fontSize: 44, fontWeight: 900, color: "#111827", letterSpacing: "-0.5px", margin: 0 }}>Honest answers.</h2>
          </div>
          <div style={{ background: "#F9FAFB", borderRadius: 20, border: "1px solid #E5E7EB", padding: "8px 28px 20px" }}>
            {FAQS.map(f => <FAQItem key={f.q} q={f.q} a={f.a} />)}
          </div>
          <div style={{ textAlign: "center", marginTop: 40 }}>
            <Link to="/signup" style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "15px 32px", background: GREEN, color: "#fff", borderRadius: 14, textDecoration: "none", fontWeight: 800, fontSize: 16 }}>
              Still curious? Try it free — no card needed
            </Link>
          </div>
        </div>
      </section>

      {/* ── 11. FINAL CTA STRIP ─────────────────────────────────────────────── */}
      <section style={{ background: GREEN, padding: "96px 32px" }}>
        <div style={{ maxWidth: 720, margin: "0 auto", textAlign: "center" }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 24 }}>
            <Logo size={60} />
          </div>
          <h2 style={{ fontSize: 48, fontWeight: 900, color: "#fff", margin: "0 0 16px", letterSpacing: "-1px", lineHeight: 1.08 }}>
            Ready to sort your taxes?
          </h2>
          <p style={{ fontSize: 18, color: "rgba(255,255,255,0.72)", margin: "0 0 40px", lineHeight: 1.65 }}>
            Upload your first statement free. TaxBuddy reads it, maps every transaction to an ATO category, and shows you what's deductible.
          </p>
          <Link to="/signup" style={{ display: "inline-flex", alignItems: "center", gap: 10, padding: "17px 40px", background: AMBER, color: "#fff", borderRadius: 14, textDecoration: "none", fontWeight: 800, fontSize: 18 }}>
            Upload my first statement free <ArrowRight size={20} />
          </Link>
          <p style={{ fontSize: 12, color: "rgba(255,255,255,0.38)", marginTop: 18 }}>
            No credit card · Australian Privacy Act compliant · Cancel anytime
          </p>
        </div>
      </section>

      {/* ── 12. FOOTER ──────────────────────────────────────────────────────── */}
      <footer style={{ background: "#111827", padding: "52px 32px 32px" }}>
        <div style={{ maxWidth: W, margin: "0 auto" }}>
          <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", alignItems: "flex-start", gap: 36, marginBottom: 40, paddingBottom: 40, borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                <Logo size={30} />
                <span style={{ fontWeight: 900, fontSize: 17, color: "#fff" }}>TaxBuddy</span>
              </div>
              <p style={{ fontSize: 13, color: "rgba(255,255,255,0.4)", lineHeight: 1.65, margin: "0 0 10px" }}>
                Australian tax categorisation tool.<br />
                Part of the FindJoy143 family.
              </p>
              <a href="mailto:findjoy143@gmail.com" style={{ fontSize: 13, color: "rgba(255,255,255,0.45)", textDecoration: "none" }}>findjoy143@gmail.com</a>
            </div>
            <div style={{ display: "flex", gap: 56 }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.28)", textTransform: "uppercase", letterSpacing: "1px", marginBottom: 16 }}>Product</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <Link to="/about" style={{ fontSize: 14, color: "rgba(255,255,255,0.55)", textDecoration: "none" }}>About</Link>
                  <Link to="/affiliate" style={{ fontSize: 14, color: "rgba(255,255,255,0.55)", textDecoration: "none" }}>Partners</Link>
                  <Link to="/login" style={{ fontSize: 14, color: "rgba(255,255,255,0.55)", textDecoration: "none" }}>Sign In</Link>
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(255,255,255,0.28)", textTransform: "uppercase", letterSpacing: "1px", marginBottom: 16 }}>Legal</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <Link to="/privacy" style={{ fontSize: 14, color: "rgba(255,255,255,0.55)", textDecoration: "none" }}>Privacy Policy</Link>
                  <Link to="/terms" style={{ fontSize: 14, color: "rgba(255,255,255,0.55)", textDecoration: "none" }}>Terms of Service</Link>
                </div>
              </div>
            </div>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: 12 }}>
            <p style={{ fontSize: 12, color: "rgba(255,255,255,0.28)", margin: 0 }}>© 2026 FindJoy143 · findjoy143@gmail.com</p>
            <p style={{ fontSize: 12, color: "rgba(255,255,255,0.22)", maxWidth: 560, textAlign: "right", margin: 0 }}>TaxBuddy is a record-keeping tool only. Not a registered tax agent. Not financial or tax advice. Always consult a qualified tax professional before lodging. Australian Consumer Law guarantees apply.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
